import React, { useEffect, useState } from "react";
import { api, resolveImageSrc } from "@/lib/api";
import { S } from "./styles";

/**
 * Buy Now (Q-commerce, COD) order modal.
 * - Confirms delivery address + GPS coords (auto-captured via browser geolocation)
 * - Creates the order; admin sees it in the Orders tab and starts delivering
 */
export default function BuyNowModal({ product, user, onClose, onPlaced, showToast }) {
  const [qty, setQty] = useState(1);
  const [address, setAddress] = useState("");
  const [mobile, setMobile] = useState(user?.mobile || "");
  const [note, setNote] = useState("");
  const [coords, setCoords] = useState(null); // {lat, lng}
  const [coordsStatus, setCoordsStatus] = useState("pending"); // pending | ok | denied
  const [placing, setPlacing] = useState(false);
  const [walletAvail, setWalletAvail] = useState(0);
  const [useWallet, setUseWallet] = useState(false);

  // Try to capture device GPS as soon as the modal opens
  useEffect(() => {
    if (!navigator?.geolocation) { setCoordsStatus("denied"); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => { setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setCoordsStatus("ok"); },
      () => setCoordsStatus("denied"),
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 }
    );
  }, []);

  useEffect(() => {
    if (!product?.shop_id) return;
    api.get(`/wallet/applicable?shop_id=${product.shop_id}`).then((r) => setWalletAvail(r.data?.applicable || 0)).catch(() => {});
  }, [product?.shop_id]);

  const unitPrice = (product?.deal_discount_pct > 0 ? product?.deal_price : product?.price) || 0;
  const total = unitPrice * qty;
  const walletUse = useWallet ? Math.min(walletAvail, total) : 0;
  const codDue = total - walletUse;

  // 5 km delivery geo-fence: a shop only delivers within 5 km of the product.
  const haversineKm = (a, b) => {
    if (!a || !b || typeof b.lat !== "number" || typeof b.lng !== "number") return null;
    const R = 6371, toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(b.lat - a.lat), dLng = toRad(b.lng - a.lng);
    const x = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
  };
  const distKm = coords ? haversineKm(coords, { lat: product?.lat, lng: product?.lng }) : null;
  const outOfRange = distKm != null && distKm > 5;

  const place = async () => {
    if (!address.trim()) { showToast?.("Please enter your delivery address."); return; }
    if (!coords) { showToast?.("We need your location to deliver. Please allow GPS."); return; }
    if (outOfRange) { showToast?.(`This shop delivers within 5 km. You're ${distKm.toFixed(1)} km away.`); return; }
    setPlacing(true);
    try {
      const { data } = await api.post("/orders", {
        product_id: product.id,
        qty,
        delivery_address: address.trim(),
        delivery_lat: coords.lat,
        delivery_lng: coords.lng,
        contact_mobile: mobile.trim() || undefined,
        note: note.trim() || undefined,
        use_wallet: useWallet,
      });
      showToast?.("✅ Order placed! Confirming stock with the shop…");
      onPlaced?.(data);
    } catch (e) {
      showToast?.(e?.response?.data?.detail || "Could not place order");
    } finally {
      setPlacing(false);
    }
  };

  const img = resolveImageSrc(product?.image);

  return (
    <div
      data-testid="buy-now-modal"
      style={{ position: "fixed", inset: 0, zIndex: 1100, display: "flex", alignItems: "flex-end", justifyContent: "center", background: "rgba(0,0,0,0.55)" }}
      onClick={onClose}
    >
      <div onClick={(e) => e.stopPropagation()} style={{ background: "white", borderRadius: "22px 22px 0 0", maxWidth: 480, width: "100%", maxHeight: "92vh", overflowY: "auto" }}>
        <div style={{ width: 40, height: 4, background: "#E0DED2", borderRadius: 4, margin: "10px auto" }} />
        <div style={{ padding: 20 }}>
          <div style={{ fontSize: 10, letterSpacing: 1.5, color: "#C97B3C", fontWeight: 700, marginBottom: 4, textTransform: "uppercase" }}>Buy Now · COD</div>
          <div style={{ ...S.fraunces, fontSize: 22, fontWeight: 700, marginBottom: 12 }}>Quick delivery</div>

          {/* Product row */}
          <div style={{ display: "flex", gap: 12, padding: 12, background: "#FAFAF5", border: "1px solid #EAE8DC", borderRadius: 14, marginBottom: 14 }}>
            {img && (img.startsWith("http") || img.startsWith("data:")) && (
              <img src={img} alt="" style={{ width: 64, height: 64, borderRadius: 10, objectFit: "cover", flexShrink: 0 }} />
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.25 }}>{product?.name}</div>
              <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>{product?.shop_name}</div>
              <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ display: "inline-flex", alignItems: "center", border: "1px solid #D5D3C7", borderRadius: 100, overflow: "hidden" }}>
                  <button data-testid="buy-now-qty-dec" onClick={() => setQty(Math.max(1, qty - 1))} style={{ border: "none", background: "white", width: 28, height: 28, fontSize: 16, cursor: "pointer" }}>−</button>
                  <span data-testid="buy-now-qty" style={{ minWidth: 24, textAlign: "center", fontSize: 13, fontWeight: 700 }}>{qty}</span>
                  <button data-testid="buy-now-qty-inc" onClick={() => setQty(qty + 1)} style={{ border: "none", background: "white", width: 28, height: 28, fontSize: 16, cursor: "pointer" }}>+</button>
                </div>
                <div style={{ marginLeft: "auto", ...S.fraunces, fontSize: 18, fontWeight: 800, color: "#0F3D2E" }}>₹{total}</div>
              </div>
            </div>
          </div>

          {/* Address */}
          <label style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: "#666" }}>DELIVERY ADDRESS</label>
          <textarea
            data-testid="buy-now-address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="House / flat no, street, landmark, area…"
            rows={3}
            style={{ width: "100%", marginTop: 6, padding: 12, borderRadius: 12, border: "1.5px solid #E0DED2", fontSize: 14, fontFamily: "inherit", resize: "vertical", boxSizing: "border-box" }}
          />

          {/* Mobile (optional) */}
          <label style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: "#666", display: "block", marginTop: 10 }}>CONTACT MOBILE</label>
          <input
            data-testid="buy-now-mobile"
            value={mobile}
            onChange={(e) => setMobile(e.target.value)}
            placeholder="10-digit mobile (optional)"
            style={{ width: "100%", marginTop: 6, padding: 12, borderRadius: 12, border: "1.5px solid #E0DED2", fontSize: 14, boxSizing: "border-box" }}
          />

          {/* Note (optional) */}
          <label style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: "#666", display: "block", marginTop: 10 }}>NOTE FOR DELIVERY (OPTIONAL)</label>
          <input
            data-testid="buy-now-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. ring the bell, gate code 123…"
            style={{ width: "100%", marginTop: 6, padding: 12, borderRadius: 12, border: "1.5px solid #E0DED2", fontSize: 14, boxSizing: "border-box" }}
          />

          {/* GPS status */}
          <div data-testid="buy-now-gps-status" style={{ marginTop: 12, padding: 10, borderRadius: 10, fontSize: 12, fontWeight: 600,
            background: coordsStatus === "ok" ? "#E8F5EE" : coordsStatus === "denied" ? "#FFEFEF" : "#FFF9F0",
            color: coordsStatus === "ok" ? "#0F3D2E" : coordsStatus === "denied" ? "#9A2C2C" : "#5C3D12",
            border: `1px solid ${coordsStatus === "ok" ? "#9BC4A8" : coordsStatus === "denied" ? "#F3B6B6" : "#F3CC8B"}`,
          }}>
            {coordsStatus === "ok" && <>📍 Location captured ({coords.lat.toFixed(4)}, {coords.lng.toFixed(4)})</>}
            {coordsStatus === "pending" && <>📍 Getting your location…</>}
            {coordsStatus === "denied" && <>⚠️ Location blocked. Please allow GPS so our rider can find you.</>}
          </div>

          {/* 5 km delivery geo-fence */}
          {coordsStatus === "ok" && distKm != null && (
            <div data-testid="buy-now-geofence" style={{ marginTop: 10, padding: 10, borderRadius: 10, fontSize: 12, fontWeight: 600,
              background: outOfRange ? "#FFEFEF" : "#E8F5EE",
              color: outOfRange ? "#9A2C2C" : "#0F3D2E",
              border: `1px solid ${outOfRange ? "#F3B6B6" : "#9BC4A8"}`,
            }}>
              {outOfRange
                ? <>🚫 Out of delivery range — this shop delivers within <b>5 km</b>, but you are <b>{distKm.toFixed(1)} km</b> away.</>
                : <>✅ Within range — about <b>{distKm.toFixed(1)} km</b> from the shop.</>}
            </div>
          )}

          {/* Wallet */}
          {walletAvail > 0 && (
            <label data-testid="buy-now-wallet-toggle" style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "center", padding: "10px 12px", borderRadius: 10, background: "#EEF6FF", border: "1px solid #BBD1F5", cursor: "pointer" }}>
              <input type="checkbox" data-testid="buy-now-wallet-checkbox" checked={useWallet} onChange={(e) => setUseWallet(e.target.checked)} />
              <span style={{ fontSize: 12.5, fontWeight: 700, color: "#1453A6" }}>👛 Use wallet · ₹{walletAvail} available{useWallet && walletUse > 0 ? ` — applying ₹${walletUse}` : ""}</span>
            </label>
          )}

          {/* COD pill */}
          <div style={{ marginTop: 12, padding: "10px 12px", borderRadius: 10, background: "#FFF9F0", border: "1px solid #F3CC8B", fontSize: 12, color: "#5C3D12", fontWeight: 600 }}>
            💵 Cash on Delivery — pay ₹{codDue} when your order arrives.{walletUse > 0 ? ` (₹${walletUse} from wallet)` : ""}
          </div>

          {/* Action */}
          <button
            data-testid="buy-now-confirm-btn"
            onClick={place}
            disabled={placing || !coords || !address.trim() || outOfRange}
            style={{
              marginTop: 16, width: "100%", padding: 14, borderRadius: 100, border: "none",
              background: (placing || !coords || !address.trim() || outOfRange) ? "#9DB3A4" : "#0F3D2E",
              color: "white", fontWeight: 700, fontSize: 15, cursor: (placing || !coords || !address.trim() || outOfRange) ? "default" : "pointer",
            }}
          >
            {placing ? "Placing order…" : outOfRange ? "🚫 Out of 5 km delivery range" : `🛵 Place order · ₹${codDue} COD`}
          </button>
          <button data-testid="buy-now-cancel-btn" onClick={onClose} style={{ marginTop: 8, width: "100%", padding: 12, borderRadius: 100, border: "1px solid #D5D3C7", background: "white", color: "#333", fontWeight: 600, cursor: "pointer" }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
